/**
 * Search — semantic search across your observations.
 *
 * UI sprint step 5 reskin (2026-05-11): port to pwa-v2 vocabulary.
 *   - `.v2-page` with `.page-head` eyebrow + title + sub
 *   - Inline search input with `Icons.Search` glyph + clear button
 *   - Results in a single `.panel` with rows that include entity-name
 *     pill, relative time, content with matched terms highlighted, and
 *     a mono score
 *
 * Behavior preserved: debounced 350ms search via POST /api/observations/
 * search (Vectorize semantic, ~no cost on free tier). Tap row → entity
 * detail. Min query length: 2 chars.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import { type SearchResult, searchObservations } from '../api.ts';
import { Icons } from '../components/icons';

interface SearchProps {
  onSelectEntity: (id: string) => void;
}

export function Search(props: SearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults(null);
      setError(null);
      setSearching(false);
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      return;
    }
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      run(query);
    }, 350) as unknown as number;
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [query]);

  async function run(q: string) {
    setSearching(true);
    setError(null);
    try {
      const r = await searchObservations(q, { topK: 20 });
      setResults(r);
    } catch (err) {
      setError((err as Error).message);
      setResults([]);
    } finally {
      setSearching(false);
    }
  }

  const isActive = query.trim().length >= 2;
  const hasResults = isActive && results !== null && results.length > 0;

  return (
    <div class="v2-page">
      <div class="page-head">
        <div>
          <div class="page-eyebrow">
            Search{hasResults && ` · ${results!.length} ${results!.length === 1 ? 'result' : 'results'}`}
          </div>
          <h1 class="v2-page-title">Find an observation</h1>
          <div class="page-sub">
            Semantic search across everything you've captured. Phrasing is
            forgiving — describe what you remember.
          </div>
        </div>
      </div>

      <div style={{ position: 'relative', marginBottom: 16 }}>
        <span
          style={{
            position: 'absolute',
            left: 14,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--text-muted)',
            display: 'inline-flex',
            pointerEvents: 'none',
          }}
        >
          <Icons.Search size={16} />
        </span>
        <input
          ref={inputRef}
          type="search"
          class="search-bar"
          placeholder="Search your portfolio…"
          value={query}
          onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
          aria-label="Search observations"
          autoFocus
          style={{
            paddingLeft: 38,
            paddingRight: query ? 38 : 14,
          }}
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery('');
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
            style={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 24,
              height: 24,
              borderRadius: '50%',
              border: 0,
              background: 'rgba(255, 255, 255, 0.06)',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: 14,
              lineHeight: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            ×
          </button>
        )}
      </div>

      {!isActive && (
        <div
          class="panel"
          style={{
            padding: 40,
            textAlign: 'center',
            color: 'var(--text-muted)',
            fontFamily: 'var(--f-serif)',
            fontStyle: 'italic',
            fontSize: 16,
            lineHeight: 1.5,
          }}
        >
          Type at least 2 characters to search.
        </div>
      )}

      {isActive && (
        <div class="panel">
          {searching && (
            <div
              style={{
                padding: 24,
                textAlign: 'center',
                fontFamily: 'var(--f-mono)',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--text-muted)',
              }}
            >
              Searching…
            </div>
          )}
          {error && !searching && (
            <div style={{ padding: 16 }}>
              <div class="error-banner" style={{ margin: 0 }}>
                {error}
              </div>
            </div>
          )}
          {!searching && !error && results !== null && results.length === 0 && (
            <div
              style={{
                padding: 32,
                textAlign: 'center',
                color: 'var(--text-muted)',
                fontFamily: 'var(--f-serif)',
                fontStyle: 'italic',
                fontSize: 15,
              }}
            >
              Nothing matched "{query}".
            </div>
          )}
          {!searching && results && results.length > 0 && (
            <div class="v2-row-list">
              {results.map((r) => (
                <div
                  key={r.observation_id}
                  class="v2-row"
                  onClick={() => props.onSelectEntity(r.entity_id)}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                        marginBottom: 4,
                      }}
                    >
                      <span class="v2-pill pill-info">
                        <span class="dot" />
                        {r.entity_name}
                      </span>
                      <span
                        class="mono"
                        style={{
                          fontSize: 10.5,
                          color: 'var(--text-faint)',
                          letterSpacing: '0.04em',
                          textTransform: 'lowercase',
                        }}
                      >
                        {r.entity_type}
                      </span>
                      <span style={{ flex: 1 }} />
                      <span
                        class="mono"
                        style={{
                          fontSize: 10.5,
                          color: 'var(--text-faint)',
                        }}
                      >
                        {relativeTime(r.observed_at)}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: 13.5,
                        color: 'var(--text-2)',
                        lineHeight: 1.5,
                        marginBottom: 4,
                      }}
                    >
                      {highlightMatch(r.content, query)}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                      }}
                    >
                      <span
                        class="mono"
                        style={{
                          fontSize: 10,
                          color:
                            r.match === 'lexical'
                              ? 'var(--ok)'
                              : 'var(--text-faint)',
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {r.match === 'lexical' ? 'exact match' : `score ${r.score.toFixed(2)}`}
                      </span>
                      {r.match === 'semantic' && (
                        <div
                          style={{
                            width: 50,
                            height: 3,
                            background: 'rgba(255,255,255,0.05)',
                            borderRadius: 2,
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              width: `${Math.min(100, r.score * 100)}%`,
                              height: '100%',
                              background: 'var(--accent)',
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Bold case-insensitive matches of query terms in the result content.
 * Pure visual helper — Vectorize ranks semantically, not lexically, so
 * the highlighted span may not always align with why this row scored
 * well; that's expected ("the model picked up X").
 */
function highlightMatch(content: string, query: string): preact.ComponentChildren {
  const terms = query
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length >= 3);
  if (terms.length === 0) return content;

  const re = new RegExp(`(${terms.map(escapeRegex).join('|')})`, 'gi');
  const parts = content.split(re);
  return parts.map((p, i) => {
    if (terms.some((t) => p.toLowerCase() === t)) {
      return (
        <mark
          key={i}
          style={{
            background: 'var(--accent-tint-22)',
            color: 'var(--accent-hi)',
            padding: '0 3px',
            borderRadius: 3,
            fontWeight: 600,
          }}
        >
          {p}
        </mark>
      );
    }
    return <span key={i}>{p}</span>;
  });
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return iso;
  const s = Math.round(ms / 1000);
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
